/**
 * 2048 PK 空间网格物理与战斗算法引擎 (Board & Combat Engine)
 * 职责：
 * 1. 4x4 网格滑移碰撞合并纯数学算法
 * 2. 联机 PK 干扰机制：故障障碍块 (Glitch Block) 植入、吸收与爆破
 * 3. 绝境预警与终局卡死严密判定
 */

import type {
  TileData,
  MoveDirection,
  BoardTheme,
} from "../../shared/protocol";

export interface Tile extends TileData {
  el?: HTMLElement;
  isNew?: boolean;
  isMerged?: boolean;
  movesRemaining?: number; // 障碍块残留回合
}

export interface LineResult {
  moved: boolean;
  earnedScore: number;
  toRemove: Tile[];
  toSpawn: Tile[];
  mergedValues: number[];
}

export const milestoneNames: Record<number, string> = {
  256: "新星 · 霓虹",
  512: "王牌 · 极电",
  1024: "大师 · 炽金",
  2048: "传奇 · 星穹",
};

export const getThemeByMaxTile = (maxTile: number): BoardTheme => {
  if (maxTile >= 2048) return "astral";
  if (maxTile >= 1024) return "obsidian";
  if (maxTile >= 512) return "titanium";
  if (maxTile >= 256) return "steel";
  return "sandstone";
};

/**
 * 4x4 滑动与合并算法 (支持常规数字块碰撞，及障碍块阻隔与震碎机制)
 */
export const processLine = (
  lineTiles: Tile[],
  isReverse: boolean,
  isVertical: boolean,
  lineIdx: number,
  getNextId: () => number,
  onTilePosChanged: (t: Tile) => void,
): LineResult => {
  const sorted = [...lineTiles].sort((a, b) => {
    const posA = isVertical ? a.r : a.c;
    const posB = isVertical ? b.r : b.c;
    return isReverse ? posB - posA : posA - posB;
  });

  let targetPos = isReverse ? 3 : 0;
  const step = isReverse ? -1 : 1;
  let earnedScore = 0;
  let moved = false;
  const toRemove: Tile[] = [];
  const toSpawn: Tile[] = [];
  const mergedValues: number[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i];
    const next = sorted[i + 1];

    // 纯粹 2048 数字合并判定：保证所有方块 100% 可滑移、同值必合并，绝不卡死手感
    if (next && cur.val > 0 && cur.val === next.val) {
      moved = true;
      const newVal = cur.val * 2;
      earnedScore += newVal;
      mergedValues.push(newVal);

      if (isVertical) {
        cur.r = targetPos;
        next.r = targetPos;
      } else {
        cur.c = targetPos;
        next.c = targetPos;
      }

      onTilePosChanged(cur);
      onTilePosChanged(next);

      toRemove.push(cur, next);

      toSpawn.push({
        id: getNextId(),
        r: isVertical ? targetPos : lineIdx,
        c: isVertical ? lineIdx : targetPos,
        val: newVal,
        isMerged: true,
        mergedIds: [cur.id, next.id],
      });

      targetPos += step;
      i++; // 消耗下一块
    } else {
      const currentPos = isVertical ? cur.r : cur.c;
      if (currentPos !== targetPos) {
        moved = true;
        if (isVertical) cur.r = targetPos;
        else cur.c = targetPos;
        onTilePosChanged(cur);
      }
      targetPos += step;
    }
  }

  return { moved, earnedScore, toRemove, toSpawn, mergedValues };
};

/**
 * 随机空位挑选
 */
export const getRandomEmptySpot = (
  tiles: Tile[],
): { r: number; c: number } | null => {
  const occupied = new Set(tiles.map((t) => `${t.r},${t.c}`));
  const empties: { r: number; c: number }[] = [];

  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      if (!occupied.has(`${r},${c}`)) {
        empties.push({ r, c });
      }
    }
  }

  if (empties.length === 0) return null;
  const idx = Math.floor(Math.random() * empties.length);
  return empties[idx];
};

/**
 * 绝境告急判定 (危险红晕预警)
 */
export const evaluateDangerState = (
  tiles: Tile[],
): { isCritical: boolean; emptyCount: number } => {
  const emptyCount = 16 - tiles.length;
  const map: number[][] = Array(4)
    .fill(0)
    .map(() => Array(4).fill(0));
  for (const t of tiles) {
    map[t.r][t.c] = t.val;
  }

  let pairs = 0;
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      const val = map[r][c];
      if (val === 0) continue;
      if (c < 3 && map[r][c + 1] === val) pairs++;
      if (r < 3 && map[r + 1][c] === val) pairs++;
    }
  }

  const isCritical = emptyCount <= 2 && pairs <= 1;
  return { isCritical, emptyCount };
};

/**
 * 终局判定：棋盘是否被填满且无法滑动
 */
export const isBoardStuck = (tiles: Tile[]): boolean => {
  if (tiles.length < 16) return false;

  const map: number[][] = Array(4)
    .fill(0)
    .map(() => Array(4).fill(0));

  for (const t of tiles) {
    if (t.r >= 0 && t.r < 4 && t.c >= 0 && t.c < 4) {
      map[t.r][t.c] = t.val;
    }
  }

  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      const val = map[r][c];
      if (val === 0) return false;

      if (c < 3 && map[r][c + 1] === val) return false;
      if (r < 3 && map[r + 1][c] === val) return false;
    }
  }

  return true;
};

/**
 * 查找封盘时的「待发射方块」（100% 保证封盘必有待发射方块）：
 * 1. 优先选择本棋盘最后一次原生生成的方块 (isLastNative === true 且 !isTransferred)，以实现被塞封盘时的空位换位洗牌
 * 2. 若本盘最后原生块此前已被发射出去（如空中交错互射场景），则兜底选择最后落入本棋盘的方块 (tiles[tiles.length - 1])
 */
export const findLastNativeTile = <T extends TileData>(
  tiles: T[],
): T | null => {
  if (tiles.length === 0) return null;
  const lastNative = tiles.find((t) => Boolean(t.isLastNative) && !t.isTransferred);
  if (lastNative) return lastNative;
  return tiles[tiles.length - 1];
};
